import { useCallback, useEffect } from "react"
import { useStaticQuery, graphql } from "gatsby"

import { useStorage } from "@app/hooks/useCore"
import { useAppContext } from "@app/providers/app"
import { useConfigContext } from "@app/providers/config"
import { useCustomerContext } from "@app/providers/customer"

import type { Location } from "@root/types/global"

export const useNewsletter = (location: Location) => {
  const { state, dispatch } = useAppContext()
  const { customer } = useCustomerContext()
  const {
    settings: { keys, routes },
    store: { subfolder },
  } = useConfigContext()
  const { getStorage, setStorage } = useStorage()

  const { newsletter } = useStaticQuery<GatsbyTypes.StaticNewsletterQuery>(graphql`
    query StaticNewsletter {
      newsletter: sanitySettingNewsletter {
        backgroundColor {
          ...NewPresetColorFragment
        }
        enabled
        delay
        expiry
        image: _rawImage(resolveReferences: { maxDepth: 6 })
        templateHomepage
        templateCollection
        templateProduct
        templateArticle
        additionalTitle
        additionalParagraph
        additionalButtonlabel
        additionalSuccess
        additionalSuccess1
        additionalSuccessButtonlabel
        additionalNameLabel
        additionalNameError
        additionalEmailLabel
        additionalEmailError
        TCtitle
        TClink {
          ...LinkFragment
        }
        enableSubscriptionWidget
        subscriptionWidgetTitle
      }
    }
  `)

  const route =
    Object.entries(routes)?.find(
      ([route, url]) => !route?.toLowerCase()?.includes("page") && url && location?.pathname?.includes(url)
    )?.[0] ||
    (location?.pathname?.replace(`${subfolder}`, "") === routes.HOMEPAGE && "HOMEPAGE")

  const active =
    // @ts-ignore
    (newsletter?.enabled &&
      !customer &&
      route &&
      newsletter[`template${route?.charAt(0)?.toUpperCase() + route?.slice(1)?.toLowerCase()}` as keyof typeof newsletter]) ||
    false

  const handleActiveSubscribe = useCallback(
    (state: boolean) => {
      dispatch({
        type: "subscribe",
        payload: state,
      })
    },
    [dispatch]
  )

  const handleClose = useCallback(() => handleActiveSubscribe(false), [handleActiveSubscribe])
  const handleOpen = useCallback(() => handleActiveSubscribe(true), [handleActiveSubscribe])

  useEffect(() => {
    let timeout: any = null

    if (active && !getStorage(keys.newsletter) && !state.activeSubscribe) {
      timeout = setTimeout(
        () => {
          setStorage(keys.newsletter, true, newsletter?.expiry || undefined)
          handleActiveSubscribe(true)
        },
        (newsletter?.delay || 0) * 1000
      )
    }

    return () => {
      if (timeout) {
        clearTimeout(timeout)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active])

  return {
    active,
    newsletter,
    activeTab: !state.activeSubscribe,
    handleClose,
    handleOpen,
    activeSubscribe: state.activeSubscribe,
  }
}
